import { useEffect, FunctionComponent, ReactElement } from 'react'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'redux/store'
import connect from 'libraries/front-end-core/decorators/connect'
import { mapResponse, isValidResponse } from 'libraries/front-end-core/helpers/dataflow'
import { map } from 'rxjs/operators'
import { $isLogged, User, UserUrn, $practisingAol, $primaryAol } from 'services/SessionService'
import {
  setUserIsLogged,
  setUserDetails,
  setUserRoles,
  setUserUrn,
  setAccessRoles,
  setAccessToSmartFeatures,
  setPrimaryAol,
  setPractisingAol,
  setPrimaryAolSetByUser
} from 'redux/actions'
import ResponseConsumer from './shared/ResponseConsumer'

const UserWrapper: FunctionComponent<UserWrapperProps> = ({
  isLogged,
  userDetails,
  userRoles,
  userUrn,
  children,
  accessRoles,
  smartFeatures,
  primaryAol,
  practisingAol
}) => {
  const dispatch = useDispatch<AppDispatch>()

  useEffect(() => {
    dispatch(setUserIsLogged(isLogged))
  }, [isLogged, setUserIsLogged])

  useEffect(() => {
    dispatch(setUserDetails(userDetails))
  }, [userDetails, setUserDetails])

  useEffect(() => {
    dispatch(setUserRoles(userRoles))
  }, [userRoles, setUserRoles])

  useEffect(() => {
    dispatch(setAccessRoles(accessRoles))
  }, [accessRoles, setAccessRoles])

  useEffect(() => {
    dispatch(setAccessToSmartFeatures(smartFeatures))
  }, [smartFeatures])

  useEffect(() => {
    if (userUrn !== '') dispatch(setUserUrn(userUrn))
  }, [userUrn])

  useEffect(() => {
    dispatch(setPrimaryAol(primaryAol))
  }, [primaryAol])

  useEffect(() => {
    dispatch(setPractisingAol(practisingAol))
  }, [practisingAol])

  useEffect(() => {
    if (userDetails?.primaryAolSetByUser !== undefined) {
      dispatch(setPrimaryAolSetByUser(userDetails.primaryAolSetByUser))
    }
  }, [userDetails?.primaryAolSetByUser, dispatch])

  const renderContent = (): JSX.Element => <>{children}</>

  return (
    <ResponseConsumer content={renderContent} pendingComponent={renderContent} />
  )
}

const ConnectedUserWrapper = connect(() => ({
  isLogged: $isLogged.pipe(map(mapResponse(({ isLogged }: { isLogged: boolean }) => isLogged))),
  userDetails: User,
  userRoles: User.pipe(map((u: any) => (isValidResponse(u) && Array.isArray(u.roles)) ? u.roles : [])),
  accessRoles: User.pipe(map((u: any) => (isValidResponse(u) && Array.isArray(u.accessRoles)) ? u.accessRoles : [])),
  smartFeatures: User.pipe(
    map(u => (isValidResponse(u) ? u.smartFeatures : undefined))
  ),
  primaryAol: $primaryAol,
  practisingAol: $practisingAol,
  userUrn: UserUrn
}))((props: any) => <UserWrapper {...props} />)

interface UserWrapperProps {
  isLogged: boolean
  userDetails: any
  userRoles: string[]
  accessRoles: string[]
  userUrn: string
  children: ReactElement
  smartFeatures: boolean
  primaryAol: string
  practisingAol: string
}

export default ConnectedUserWrapper
